import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Field, Th, Td, TableWrap, NativeSelect } from "@/components/kit";
import { dmy, todayIso, errMsg, r3 } from "@/lib/format";

const n3 = (v) => (v ? r3(v).toLocaleString("fr-FR", { minimumFractionDigits: 3 }) : "");
const JOURNALS = ["VT", "AC", "BQ", "CA", "OD", "CL", "AN"];

const EntryDialog = ({ open, onClose, accounts, onSaved }) => {
  const { api, t } = useApp();
  const [f, setF] = useState({ journal: "OD", date: todayIso(), label: "", lines: [{ account: "", debit: 0, credit: 0 }, { account: "", debit: 0, credit: 0 }] });
  const upd = (i, p) => setF({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, ...p } : l)) });
  const d = r3(f.lines.reduce((s, l) => s + (+l.debit || 0), 0));
  const c = r3(f.lines.reduce((s, l) => s + (+l.credit || 0), 0));
  const save = async () => {
    try {
      await api.post("/entries", { ...f, lines: f.lines.map((l) => ({ ...l, debit: +l.debit || 0, credit: +l.credit || 0 })) });
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader><DialogTitle>{t("new_entry")}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t("journal")}><NativeSelect value={f.journal} onChange={(e) => setF({ ...f, journal: e.target.value })} data-testid="entry-journal">{JOURNALS.map((j) => <option key={j}>{j}</option>)}</NativeSelect></Field>
          <Field label={t("date")}><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="entry-date" /></Field>
          <Field label={t("label")}><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="entry-label" /></Field>
        </div>
        {f.lines.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_110px_110px_36px] gap-2">
            <NativeSelect value={l.account} onChange={(e) => upd(i, { account: e.target.value })} data-testid={`entry-account-${i}`}><option value="">—</option>{accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.label}</option>)}</NativeSelect>
            <Input type="number" step="0.001" placeholder={t("debit")} value={l.debit || ""} onChange={(e) => upd(i, { debit: e.target.value })} data-testid={`entry-debit-${i}`} />
            <Input type="number" step="0.001" placeholder={t("credit")} value={l.credit || ""} onChange={(e) => upd(i, { credit: e.target.value })} data-testid={`entry-credit-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <div className="flex items-center justify-between text-sm">
          <Button size="sm" variant="outline" onClick={() => setF({ ...f, lines: [...f.lines, { account: "", debit: 0, credit: 0 }] })} data-testid="entry-add-line"><Plus className="h-4 w-4 me-1" />{t("add_line")}</Button>
          <span className={`num ${d === c && d > 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="entry-balance">{n3(d)} / {n3(c)}</span>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={save} disabled={d !== c || d === 0} data-testid="entry-save">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SchemeEditor = ({ accounts }) => {
  const { api, t, can } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/accounting/scheme").then((r) => setS(r.data.scheme)); }, [api]);
  if (!s) return <Loading />;
  const save = async () => { try { await api.put("/accounting/scheme", s); toast.success(t("saved")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Object.keys(s).map((k) => (
          <Field key={k} label={k}><NativeSelect value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} disabled={!can("accounting", true)} data-testid={`scheme-${k}`}>{accounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.label}</option>)}</NativeSelect></Field>
        ))}
      </div>
      {can("accounting", true) && <Button className="mt-4" onClick={save} data-testid="scheme-save">{t("save")}</Button>}
    </>
  );
};

export default function Accounting() {
  const { api, t, can, download } = useApp();
  const [tab, setTab] = useState("journal");
  const [range, setRange] = useState({ from: `${todayIso().slice(0, 4)}-01-01`, to: todayIso() });
  const [journal, setJournal] = useState("");
  const [account, setAccount] = useState("");
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [entryOpen, setEntryOpen] = useState(false);
  const loadAccounts = useCallback(() => api.get("/accounts").then((r) => setAccounts(r.data)), [api]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  const load = useCallback(() => {
    setData(null);
    const p = { ...range };
    if (tab === "journal") api.get("/entries", { params: { ...p, journal: journal || undefined } }).then((r) => setData(r.data));
    if (tab === "ledger") api.get("/ledger", { params: { ...p, account: account || undefined } }).then((r) => setData(r.data));
    if (tab === "balance") api.get("/balance", { params: p }).then((r) => setData(r.data));
    if (["accounts", "scheme"].includes(tab)) setData([]);
  }, [api, tab, range, journal, account]);
  useEffect(() => { load(); }, [load]);
  const addSub = async () => {
    const code = window.prompt(`${t("code")} (ex. 5321)`);
    if (!code) return;
    const label = window.prompt(t("label"));
    try { await api.post("/accounts", { code, label: label || code }); loadAccounts(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exp = (fmt) => download(`/accounting/export/${tab}?format=${fmt}&from=${range.from}&to=${range.to}`, `${tab}.${fmt}`, false);
  const srcLink = (s) => (s && s.type !== "payment" && s.type !== "cashing" ? <Link className="underline" to={`/app/documents/${s.id}`}>↗</Link> : null);
  return (
    <>
      <PageHeader eyebrow={t("accounting")} title={t("accounting")}>
        <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="w-40" data-testid="acc-from" />
        <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="w-40" data-testid="acc-to" />
        {["journal", "ledger", "balance"].includes(tab) && <><Button variant="outline" onClick={() => exp("xlsx")} data-testid="acc-export-xlsx"><Download className="h-4 w-4 me-1" />Excel</Button><Button variant="outline" onClick={() => exp("pdf")} data-testid="acc-export-pdf"><Download className="h-4 w-4 me-1" />PDF</Button></>}
        {can("accounting", true) && <Button onClick={() => setEntryOpen(true)} data-testid="acc-new-entry"><Plus className="h-4 w-4 me-1" />{t("new_entry")}</Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border flex-wrap h-auto">
          {["journal", "ledger", "balance", "accounts", "scheme"].map((k) => <TabsTrigger key={k} value={k} data-testid={`acc-tab-${k}`}>{t(k === "accounts" ? "chart_accounts" : k)}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <p className="text-xs text-slate-500">{t("disclaimer")}</p>
      <Panel>
        {!data || (tab === "balance") !== !Array.isArray(data) ? <Loading /> : tab === "journal" ? (
          <>
            <NativeSelect className="mb-4" value={journal} onChange={(e) => setJournal(e.target.value)} data-testid="acc-journal-filter"><option value="">{t("all")}</option>{JOURNALS.map((j) => <option key={j}>{j}</option>)}</NativeSelect>
            {data.length === 0 ? <Empty /> : (
              <TableWrap><thead className="border-b"><tr><Th>{t("date")}</Th><Th>{t("journal")}</Th><Th>Pièce</Th><Th>{t("account")}</Th><Th>{t("label")}</Th><Th className="text-end">{t("debit")}</Th><Th className="text-end">{t("credit")}</Th></tr></thead>
                <tbody>{data.map((e) => e.lines.map((l, i) => (
                  <tr key={e.id + i} className={`${i === e.lines.length - 1 ? "border-b border-slate-200" : ""}`} data-testid={i === 0 ? `entry-${e.ref}` : undefined}>
                    <Td className="num text-xs">{i === 0 ? dmy(e.date) : ""}</Td><Td className="text-xs font-semibold">{i === 0 ? e.journal : ""}</Td><Td className="num text-xs">{i === 0 ? <>{e.ref} {srcLink(e.source)}</> : ""}</Td>
                    <Td className="num text-xs">{l.account}</Td><Td className="text-xs">{l.label}</Td><Td className="text-end num text-xs">{n3(l.debit)}</Td><Td className="text-end num text-xs">{n3(l.credit)}</Td>
                  </tr>)))}</tbody></TableWrap>
            )}
          </>
        ) : tab === "ledger" ? (
          <>
            <Input className="mb-4 w-48" placeholder={`${t("account")} (ex. 411)`} value={account} onChange={(e) => setAccount(e.target.value)} data-testid="acc-ledger-account" />
            {data.length === 0 ? <Empty /> : data.map((a) => (
              <div key={a.account} className="mb-6" data-testid={`ledger-${a.account}`}>
                <div className="mb-2 flex justify-between text-sm font-semibold"><span><span className="num">{a.account}</span> — {a.label}</span><span className="num">{t("solde")} {n3(a.balance) || "0,000"}</span></div>
                <TableWrap><tbody>{a.lines.map((l, i) => (
                  <tr key={i} className="border-b border-slate-50"><Td className="num text-xs w-24">{dmy(l.date)}</Td><Td className="text-xs w-12">{l.journal}</Td><Td className="num text-xs w-36">{l.ref}</Td><Td className="text-xs">{l.label}</Td>
                    <Td className="text-end num text-xs w-28">{n3(l.debit)}</Td><Td className="text-end num text-xs w-28">{n3(l.credit)}</Td><Td className="text-end num text-xs w-28 font-semibold">{n3(l.balance)}</Td></tr>))}</tbody></TableWrap>
              </div>))}
          </>
        ) : tab === "balance" ? (
          data.rows.length === 0 ? <Empty /> : (
            <TableWrap><thead className="border-b"><tr><Th>{t("account")}</Th><Th>{t("label")}</Th><Th className="text-end">{t("debit")}</Th><Th className="text-end">{t("credit")}</Th><Th className="text-end">{t("solde")}</Th></tr></thead>
              <tbody>{data.rows.map((r) => <tr key={r.account} className="border-b border-slate-50" data-testid={`balance-${r.account}`}><Td className="num text-xs">{r.account}</Td><Td>{r.label}</Td><Td className="text-end num">{n3(r.debit)}</Td><Td className="text-end num">{n3(r.credit)}</Td><Td className="text-end num font-semibold">{n3(r.balance)}</Td></tr>)}
                <tr className="bg-slate-50 font-bold"><Td /><Td>Total</Td><Td className="text-end num" data-testid="balance-total-debit">{n3(data.total_debit)}</Td><Td className="text-end num" data-testid="balance-total-credit">{n3(data.total_credit)}</Td><Td /></tr></tbody></TableWrap>
          )
        ) : tab === "accounts" ? (
          <>
            {can("accounting", true) && <Button variant="outline" className="mb-4" onClick={addSub} data-testid="acc-add-subaccount"><Plus className="h-4 w-4 me-1" />{t("add_subaccount")}</Button>}
            <div className="grid gap-x-8 sm:grid-cols-2">{accounts.map((a) => <div key={a.code} className="flex gap-3 border-b border-slate-50 py-1.5 text-sm"><span className="num w-16 font-semibold">{a.code}</span><span>{a.label}</span>{a.custom && <span className="ms-auto text-[10px] text-teal-700">+</span>}</div>)}</div>
          </>
        ) : <SchemeEditor accounts={accounts} />}
      </Panel>
      <EntryDialog open={entryOpen} onClose={() => setEntryOpen(false)} accounts={accounts} onSaved={load} />
    </>
  );
}
